import { pausableWatch, useEventListener } from '@vueuse/core';
import { parse } from 'qs';
import { stringifyQueryString } from '~/utils/string';
import { reactive, type Reactive } from 'vue';

function isObject(val: unknown): val is Record<string, unknown> {
    return val != undefined && val.constructor === Object;
}

function isNumber(val: unknown): val is number {
    return (
        (typeof val === 'string' || typeof val === 'number') &&
        !isNaN(parseFloat(val as string)) &&
        isFinite(val as number)
    );
}

function isBoolean(val: unknown): boolean {
    return val === 'false' || val === 'true';
}

function isArray(val: unknown): val is unknown[] {
    return Array.isArray(val);
}

function parseValue(val: unknown): typeof val {
    if (typeof val == 'undefined' || val == '' || val === null) {
        return null;
    } else if (isBoolean(val)) {
        return parseBoolean(val);
    } else if (isArray(val)) {
        return parseArray(val);
    } else if (isObject(val)) {
        return parseObject(val);
    } else if (isNumber(val)) {
        return parseNumber(val);
    } else {
        return val;
    }
}

function parseObject(obj: Record<string, unknown>): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    let key: string, val: unknown;
    for (key in obj) {
        val = parseValue(obj[key]);
        if (val !== null) {
            result[key] = val;
        }
    }
    return result;
}

function parseArray(arr: unknown[]): unknown[] {
    const result: unknown[] = [];
    for (let i = 0; i < arr.length; i++) {
        result[i] = parseValue(arr[i]);
    }
    return result;
}

function parseNumber(val: unknown): number {
    return Number(val);
}

function parseBoolean(val: unknown): boolean {
    return val === 'true';
}

interface UseQueryParamsOptions<T> {
    initialValue?: T;
    write?: boolean;
}

// code adapted from https://github.com/vueuse/vueuse/blob/main/packages/core/useUrlSearchParams/index.ts
// this matches php style for query params using `qs` package
// removed hash logic
export function useQueryParams<T extends object>(options: UseQueryParamsOptions<T> = {}): Reactive<T> {
    const { initialValue = {} as T, write: enableWrite = true } = options;

    const state = reactive<T>({} as T);

    function getRawParams(): string {
        const rawParams = window.location.search || '';
        return rawParams.startsWith('?') ? rawParams.slice(1) : rawParams;
    }

    function constructQuery(params: T): string {
        return stringifyQueryString(params);
    }

    function read(): T {
        const rawParams = getRawParams();

        // @ts-expect-error TODO: Improve the types
        return parseObject(rawParams ? parse(rawParams, {}) : initialValue) as T;
    }

    function updateState(params: T): void {
        Object.assign(state, JSON.parse(JSON.stringify(params)));
    }

    const { pause, resume } = pausableWatch(
        state,
        () => {
            write(JSON.parse(JSON.stringify(state)));
        },
        { deep: true }
    );

    function write(params: T, shouldUpdate?: boolean): void {
        pause();

        if (shouldUpdate) {
            updateState(params);
        }

        const query = constructQuery(params);

        window.history.replaceState(
            window.history.state,
            window.document.title,
            window.location.pathname + (query ? '?' + query : '') + window.location.hash
        );

        resume();
    }

    function onChanged(): void {
        if (!enableWrite) {
            return;
        }

        write(read(), true);
    }

    useEventListener(window, 'popstate', onChanged, false);

    const initial = read();
    Object.assign(state, initial);

    return state;
}
